import { useEffect } from 'react';
import { useNavbar } from '@app/atoms/app/navbar.atom.ts';
import {
  addToast,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/react';
import {
  useProfileUpdate,
  useRemoveProfile,
} from '../../entites/authorization';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useUser } from '../../entites/authorization/use-user.ts';
import { mask } from '@shared/const/mask.ts';
import { InfoCircle } from 'iconsax-react';
import { useAtomValue } from 'jotai/index';
import { fcmAtom } from '@app/atoms/app/fcm.atom.ts';

type Form = {
  phone: string;
  firstName: string;
  lastName: string;
};

export const EditProfilePage = () => {
  const set = useNavbar();

  const deleteModal = useDisclosure();
  const { user, logout } = useUser();

  const fcm_token = useAtomValue(fcmAtom);

  const { trigger, isMutating } = useProfileUpdate({
    onSuccess: () =>
      addToast({
        radius: 'full',
        color: 'success',
        variant: 'solid',
        title: 'Данные сохранены',
        closeIcon: false,
        size: 'lg',
        icon: (
          <div>
            <InfoCircle
              size="24"
              color="white"
              className="rotate-180 text-white"
            />
          </div>
        ),
        classNames: {
          title: 'text-white',
        },
      }),
  });
  const remove = useRemoveProfile({
    onSuccess: logout,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>();

  const onSubmit: SubmitHandler<Form> = async (props) => {
    const localFcmToken = localStorage.getItem('fcmToken');
    trigger({
      first_name: props.firstName,
      last_name: props.lastName,
      fcm_token: fcm_token || localFcmToken || '',
    });
  };

  useEffect(() => {
    set.editProfile();
  }, []);

  return (
    <>
      {user && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="pt-4 pb-24 flex flex-col items-center"
        >
          <Input
            defaultValue={mask(user!.phone)}
            type="tel"
            disabled
            placeholder="Номер телефона"
            size="lg"
            labelPlacement="inside"
          />

          <Input
            {...register('firstName')}
            defaultValue={user?.first_name || undefined}
            placeholder="Имя"
            classNames={{
              innerWrapper: 'h-14',
            }}
            size="lg"
            className="mt-4"
            labelPlacement="inside"
            isInvalid={!!errors.firstName?.message}
            errorMessage={errors.firstName?.message}
          />

          <Input
            {...register('lastName')}
            defaultValue={user?.last_name || undefined}
            placeholder="Фамилия"
            classNames={{
              innerWrapper: 'h-14',
            }}
            size="lg"
            className="mt-4"
            labelPlacement="inside"
            isInvalid={!!errors.lastName?.message}
            errorMessage={errors.lastName?.message}
          />

          <Button
            type="submit"
            isLoading={isMutating}
            fullWidth
            color="primary"
            size="lg"
            className="mt-6"
          >
            Сохранить
          </Button>

          <Button
            type="button"
            fullWidth
            variant="light"
            color="danger"
            size="lg"
            className="mt-6"
            onPress={deleteModal.onOpen}
          >
            Удалить аккаунт
          </Button>
        </form>
      )}

      <Modal
        placement="center"
        size="2xl"
        hideCloseButton
        isOpen={deleteModal.isOpen}
        onOpenChange={deleteModal.onOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-row items-center gap-x-3">
                <InfoCircle size="24" color="#F53639" className="rotate-180" />

                <p className="text-[#222222] text-lg font-semibold">
                  Удалить аккаунт?
                </p>
              </ModalHeader>
              <ModalBody>
                <p className="text-[#60656C] text-sm">
                  Аккаунт будет удален через 30 дней. По истечении этого срока
                  все данные будут безвозвратно удалены. Если вы войдёте в
                  аккаунт в течение этого времени, удаление будет отменено.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button
                  size="lg"
                  color="default"
                  variant="solid"
                  onPress={onClose}
                >
                  Отмена
                </Button>
                <Button
                  isLoading={remove.isMutating}
                  size="lg"
                  fullWidth
                  color="danger"
                  variant="solid"
                  onPress={() => remove.trigger()}
                >
                  Удалить аккаунт
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
